import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchParts, deletePart } from '@/lib/api'
import type { Part } from '@/lib/types'

interface PartRow extends Part {
  vendor?: { name: string; slug: string } | null
}

export default function PartsList() {
  const [parts, setParts] = useState<PartRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try {
      const data = await fetchParts()
      setParts(data as PartRow[])
    } catch { /* noop */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleDelete(id: string, slug: string) {
    if (!confirm(`Delete part "${slug}"? This cannot be undone.`)) return
    setDeleting(id)
    try {
      await deletePart(id)
      await load()
    } catch { /* noop */ }
    setDeleting(null)
  }

  const filtered = parts.filter(p =>
    !search ||
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.brand.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.vendor?.name || '').toLowerCase().includes(search.toLowerCase())
  )

  const conditionBadge: Record<string, string> = {
    New: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Grade A': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    'Grade B': 'bg-amber-50 text-amber-700 border-amber-200',
    Used: 'bg-slate-100 text-slate-600 border-slate-200',
  }

  return (
    <Layout title="Parts"
      action={<a href="/parts/new" className="inline-flex items-center gap-2 bg-brand-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
        Add Part
      </a>}
    >
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Search */}
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search parts, brand, category, vendor..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 text-left">
                  <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Part</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Condition</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Price (AED)</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No parts found.</td></tr>
                ) : filtered.map(p => (
                  <tr key={p.id} className="border-b border-surface-100 hover:bg-surface-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {p.photo ? (
                          <img src={p.photo} alt="" className="w-11 h-11 rounded-lg object-cover border border-surface-200" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-surface-100 flex items-center justify-center text-slate-300">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{p.title}</p>
                          <p className="text-xs text-slate-500">{p.brand} {p.model ? `· ${p.model}` : ''} {p.year ? `· ${p.year}` : ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{p.vendor?.name ?? '—'}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${conditionBadge[p.condition] ?? conditionBadge.Used}`}>{p.condition}</span>
                    </td>
                    <td className="px-5 py-3.5 font-semibold text-slate-900">{Number(p.price_aed).toLocaleString()} AED</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${p.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${p.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        {p.is_active ? 'Active' : 'Hidden'}
                        {p.featured && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-200">FEATURED</span>}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <a href={`/parts/${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700 mr-3">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        Edit
                      </a>
                      <button
                        onClick={() => handleDelete(p.id, p.slug)}
                        disabled={deleting === p.id}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                        {deleting === p.id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  )
}
